import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { login } from "@src/api/sign";
import LeftBox from "@src/component/LeftBox";
import SignButton from "@src/component/SignButton";
import { BackGround, Content, EmptySpace, Layout } from "@src/component/SignComponent";
import { ChatInterface } from "@src/component/v2/ChatInterface";
import { LeadCaptureModal } from "@src/component/v2/LeadCaptureModal";
import useLoader from "@src/hook/useLoader";
import useUser from "@src/hook/useUser";
import {
	defaultMessageAtom,
	globalSnackbarState,
	initPage,
	isApplyOpenAtom,
	isChatOpenAtom,
	userInfoAtom
} from "@src/state";
import { emailRule, passwordRule } from "@src/utils/rules/common";
import { MetaTag } from "@src/utils/types/meta";
import type { NextPage, NextPageContext } from "next";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import { Controller, useForm } from "react-hook-form";
import { useRecoilValue, useSetRecoilState } from "recoil";

interface SignInProps extends MetaTag {
	type: string;
}

interface UserLoginForm {
	email: string;
	password: string;
}

const SignIn: NextPage<SignInProps> = (props) => {
	const { type } = props;
	let signInText = "전문가 로그인";
	if (type === "client") {
		signInText = "고객사 로그인";
	}

	const initPageData = useRecoilValue(initPage);
	const setIsApplyOpen = useSetRecoilState(isApplyOpenAtom);
	const setIsChatOpen = useSetRecoilState(isChatOpenAtom);
	const userInfo = useRecoilValue(userInfoAtom);
	const setUserInfo = useSetRecoilState(userInfoAtom);
	const setDefaultMessage = useSetRecoilState(defaultMessageAtom);
	const isApplyOpen = useRecoilValue(isApplyOpenAtom);
	const isChatOpen = useRecoilValue(isChatOpenAtom);
	const defaultMessage = useRecoilValue(defaultMessageAtom);

	const router = useRouter();
	const withLoader = useLoader();
	const { handleSubmit, control, setValue, formState } = useForm<UserLoginForm>({
		defaultValues: {
			email: "",
			password: "",
		},
		mode: "onBlur",
	});

	const structuredData = {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement: [
			{
				"@type": "ListItem",
				position: 1,
				name: "Home",
				item: "https://1point.kr",
			},
			{
				"@type": "ListItem",
				position: 2,
				name: "로그인",
				item: "https://1point.kr/signin/",
			},
		],
	};
	const { getUserData } = useUser();

	const setSnackbareState = useSetRecoilState(globalSnackbarState);
	const [openInfoDialog, setOpenInfoDialog] = useState(false);
	const [dialogMessage, setDialogMessage] = useState("");

	useEffect(() => {
		// 쿼리 파라미터로 팝업 표시 여부 확인
		if (router.query.showInfoDialog === "true") {
			setOpenInfoDialog(true);
			// dialogType에 따라 메시지 설정
			const dialogType = router.query.dialogType as string;
			if (dialogType === "phoneConsult") {
				setDialogMessage(
					"신청해주셔서 감사드립니다. 빠른 시일내에 컨설턴트가 전화 드리도록 하겠습니다.\n진행을 위해 먼저 로그인을 진행해주세요.",
				);
			} else {
				// default: 기존 메시지
				setDialogMessage(
					"대화내용 제출 감사합니다. 최종 신청을 위해 매칭신청서 제출이 필요합니다.\n먼저 로그인을 진행해주세요.",
				);
			}
		}
	}, [router.query]);

	const onClickSignIn = async (value: UserLoginForm) => {
		try {
			const loginResult = await login(value.email, value.password, type === "client" ? 1 : 2);
			const profileData = await getUserData();

			const tagManagerArgs = {
				dataLayer: {
					event: `${type}-signin-end`,
				},
			};
			TagManager.dataLayer(tagManagerArgs);

			if (initPageData) {
				router.replace(initPageData);
				return;
			}

			if (profileData) {
				router.replace("/match/my");
				return;
			}

			if (type === "client") {
				router.push("/match/my");
			} else {
				router.push("/expert-application");
			}
		} catch (error: any) {
			setValue("password", "");
			setSnackbareState(error.errorMessage || error.message);
		}
	};

	return (
		<>
			<Dialog open={openInfoDialog} onClose={() => setOpenInfoDialog(false)}>
				<DialogTitle>안내</DialogTitle>
				<DialogContent>
					{dialogMessage.split("\n").map((line, index) => (
						<div key={index}>
							{line}
							{index < dialogMessage.split("\n").length - 1 && <br />}
						</div>
					))}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenInfoDialog(false)}>확인</Button>
				</DialogActions>
			</Dialog>
			<Head>
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
				></script>
			</Head>
			<BackGround>
				<Layout>
					<LeftBox type={type} />
					<Content>
						<form className="container" onSubmit={handleSubmit(onClickSignIn)}>
							<div className="title">{signInText}</div>
							<EmptySpace space={35} />
							<Controller
								name="email"
								control={control}
								defaultValue=""
								rules={emailRule}
								render={({ field: { ref, ...field }, fieldState: { error } }) => (
									<TextField
										label="이메일"
										fullWidth
										placeholder="hello@1point.kr"
										inputRef={ref}
										{...field}
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
							<EmptySpace space={16} />
							<Controller
								name="password"
								control={control}
								defaultValue=""
								rules={passwordRule}
								render={({ field: { ref, ...field }, fieldState: { error } }) => (
									<TextField
										label="비밀번호"
										fullWidth
										type="password"
										placeholder="* * * * * * * * *"
										inputRef={ref}
										{...field}
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
							<EmptySpace space={8} />
							<Link href={`/id/find?type=${type}`}>
								<a className="link">아이디를 잊으셨나요?</a>
							</Link>
							<span
								className="link-divider"
								style={{ fontSize: "12px", color: "gray" }}
							>
								{" "}
								|{" "}
							</span>
							<Link href={`/password/find?type=${type}`}>
								<a
									className="link"
									style={{
										fontWeight: 600,
										color: "var(--primary)",
									}}
								>
									비밀번호 찾기/변경
								</a>
							</Link>
							<EmptySpace space={16} />
							<SignButton
								text="로그인 하기"
								type="submit"
								loading={formState.isSubmitting}
							></SignButton>
							<EmptySpace space={32} />
							{type !== "client" ? (
								<>
									<div
										className={`goJoin btnc`}
										onClick={() => {
											router.push(`/signup?type=${type}`);
										}}
									>
										아직 전문가가 아니신가요? <span>1분 만에 가입하기</span>
									</div>
									<EmptySpace space={16} />
								</>
							) : (
								<>
									<div
										className={`goJoin btnc`}
										onClick={() => {
											const tagManagerArgs = {
												dataLayer: {
													event: `client-apply-start`,
												},
											};
											if (!window.localStorage.getItem("accessToken")) {
												TagManager.dataLayer(tagManagerArgs);
											}
											setDefaultMessage("");
											if (userInfo.name) {
												setIsChatOpen(true);
											} else {
												setIsApplyOpen(true);
											}
										}}
									>
										처음이라면? <span>신청하고 가입하기</span>
									</div>
									<EmptySpace space={16} />
								</>
							)}
							<div
								className={`goJoin btnc`}
								onClick={() => {
									router.replace(
										`/signin?type=${type === "client" ? "expert" : "client"}`,
									);
								}}
							>
								<span>
									{type === "client" ? "전문가로 로그인" : "고객사로 로그인"}
								</span>
							</div>
						</form>
					</Content>
				</Layout>
			</BackGround>
			
			<LeadCaptureModal
				isOpen={isApplyOpen}
				onClose={() => setIsApplyOpen(false)}
				onSubmit={async (data) => {
					setUserInfo(data);
					setIsApplyOpen(false);
					setIsChatOpen(true);
				}}
			/>
			<ChatInterface
				isOpen={isChatOpen}
				onClose={() => setIsChatOpen(false)}
				userInfo={userInfo}
				defaultMessage={defaultMessage}
			/>
		</>
	);
};

SignIn.getInitialProps = async (ctx: NextPageContext) => {
	const result: SignInProps = {
		type: ["client", "expert"].includes(ctx.query.type as string)
			? (ctx.query.type as string)
			: "client",
		meta: {
			title: "One Point - 로그인",
			metaTitle: "One Point - 로그인",
			metaDescription: "원포인트(One Point)의 로그인 페이지입니다.",
			metaOgType: "website",
			metaOgTitle: "One Point - 로그인",
			metaOgDescription: "원포인트(One Point)의 로그인 페이지입니다.",
			metaOgImage: "/static/image/metadata/thumbnail0424.png",
			metaOgUrl: "https://www.1point.kr/signin",
			metaTwitterCard: "summary",
			metaTwitterTitle: "One Point - 로그인",
			metaTwitterDescription: "원포인트(One Point)의 로그인 페이지입니다.",
			metaTwitterImage: "/static/image/metadata/thumbnail0424.png",
		},
	};

	return result;
};

export default SignIn;